/**
 * The Watchlist filter vocabulary — the six collapsible sections (Basic,
 * Financial, Fit & Ratings, Performance, Goalkeeper, Report Stats) with no
 * drawer chrome around them.
 *
 * SE-8608: this is the ONE copy of the section list. The Watchlist drawer
 * (`FilterSheetView`) and the pipeline's filter panel
 * (`AddPlayersFilterPanelView`, behind the Table/Kanban `TableFilterDrawer`
 * and the add-players drawer) both render it. SE-6871/SE-7144 had converged
 * the two on the same molecules but kept two section lists, and the pipeline
 * copy silently missed REPORT STATS and the "Current Season GPR" placeholder
 * when the Watchlist gained them. A field added here now lands on both.
 *
 * Stateless: every value and handler arrives from `useFilterPanelLogic`
 * through `toFilterSheetSectionsProps`, which both containers share.
 * @module features/scouting/components/organisms/FilterSheetSections/FilterSheetSectionsView
 */

import React, { memo } from "react";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
} from "@/constants/filterDefaults";

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import FilterChip from "@/features/scouting/components/molecules/FilterChip";
import CustomMetricFilterFields from "@/features/scouting/components/organisms/CustomMetricFilterFields";
import RangeFilterField from "@/components/organisms/shared/RangeFilterField";
import RoleChipGrid from "@/features/scouting/components/molecules/RoleChipGrid";
import SectionHeader from "@/features/scouting/components/molecules/SectionHeader";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import formatNumberWithCommas from "@/utils/formatNumberWithCommas";

/** The sections a host can hang a test id on. */
export type FilterSheetSectionKey =
  | "basic"
  | "financial"
  | "fitAndRatings"
  | "performance"
  | "goalkeeper"
  | "reportStats";

/** No-op callback used for disabled filter controls */
const NOOP = () => {};

/**
 * Returns elevated z-index when a filter dropdown is open, base otherwise
 * @param isOpen - Whether the dropdown is currently open
 */
const filterZ = (isOpen: boolean): string => (isOpen ? "z-50" : "z-0");

/**
 * The label above each chip filter (16px Regular: no ratified text style yet,
 * see docs/design-system/typography-inventory.md).
 * @param label - The filter name.
 * @returns The label element.
 */
const filterLabel = (label: string) => (
  <Text variant="unstyled" className="text-title-sm text-content-muted">
    {label}
  </Text>
);

/** Props for {@link FilterSheetSectionsView}. */
export interface FilterSheetSectionsProps {
  readonly discoveryOnly?: boolean;
  readonly sportingOnly?: boolean;
  /** Whether filter option queries are still loading */
  readonly optionsLoading: boolean;
  /** Test id for a section header — hosts with e2e journeys supply one. */
  readonly sectionTestID?: (key: FilterSheetSectionKey) => string;
  /** Test id for a range row, keyed by field (`age`, `cost`, `gpr`, …). */
  readonly rangeTestID?: (key: string) => string;

  /* --- Basic Filters section --- */
  readonly isBasicOpen: boolean;
  readonly onToggleBasic: () => void;
  readonly basicFilterCount: number;

  readonly positionOptions: readonly FilterOption[];
  readonly selectedPositionIds: readonly string[];
  readonly onTogglePosition: (id: string) => void;
  readonly onClearPositions: () => void;
  readonly isPositionFilterOpen: boolean;
  readonly onTogglePositionFilter: () => void;
  readonly onClosePositionFilter: () => void;

  readonly clubOptions: readonly FilterOption[];
  readonly selectedClubIds: readonly string[];
  readonly onToggleClub: (id: string) => void;
  readonly onClearClubs: () => void;
  readonly isClubFilterOpen: boolean;
  readonly onToggleClubFilter: () => void;
  readonly onCloseClubFilter: () => void;
  /** Server-side search callback for clubs — debounced inside FilterChip */
  readonly onServerSearchClub?: (text: string) => void;
  /** Whether club search is currently loading */
  readonly clubSearching?: boolean;

  readonly nationalityOptions: readonly FilterOption[];
  readonly selectedNationalityIds: readonly string[];
  readonly onToggleNationality: (id: string) => void;
  readonly onClearNationalities: () => void;
  readonly isNationalityFilterOpen: boolean;
  readonly onToggleNationalityFilter: () => void;
  readonly onCloseNationalityFilter: () => void;

  readonly leagueOptions: readonly FilterOption[];
  readonly selectedLeagueIds: readonly string[];
  readonly onToggleLeague: (id: string) => void;
  readonly onClearLeagues: () => void;
  readonly isLeagueFilterOpen: boolean;
  readonly onToggleLeagueFilter: () => void;
  readonly onCloseLeagueFilter: () => void;
  readonly roleOptions: readonly FilterOption[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly onToggleRole: (id: string) => void;
  readonly onClearRoles: () => void;

  readonly ageRange: [number, number];
  readonly onSetAgeRange: (range: [number, number]) => void;
  readonly contractMonthsRange: [number, number];
  readonly onSetContractMonthsRange: (range: [number, number]) => void;

  /* --- Financial section --- */
  readonly isFinancialOpen: boolean;
  readonly onToggleFinancial: () => void;
  readonly financialFilterCount: number;
  readonly valuationRange: [number, number];
  readonly onSetValuationRange: (range: [number, number]) => void;

  /* --- Fit & Ratings section --- */
  readonly isFitRatingsOpen: boolean;
  readonly onToggleFitRatings: () => void;
  readonly fitRatingsFilterCount: number;
  readonly gprRange: [number, number];
  readonly onSetGprRange: (range: [number, number]) => void;
  readonly fitScoreRange: [number, number];
  readonly onSetFitScoreRange: (range: [number, number]) => void;
  readonly teamStyleFitRange: [number, number];
  readonly onSetTeamStyleFitRange: (range: [number, number]) => void;

  /* --- Performance section --- */
  readonly isPerformanceOpen: boolean;
  readonly onTogglePerformance: () => void;
  readonly performanceFilterCount: number;
  readonly carryingRange: [number, number];
  readonly onSetCarryingRange: (range: [number, number]) => void;
  readonly dribblingRange: [number, number];
  readonly onSetDribblingRange: (range: [number, number]) => void;
  readonly passingRange: [number, number];
  readonly onSetPassingRange: (range: [number, number]) => void;
  readonly shootingRange: [number, number];
  readonly onSetShootingRange: (range: [number, number]) => void;

  /* --- Goalkeeper section --- */
  readonly isGoalkeeperOpen: boolean;
  readonly onToggleGoalkeeper: () => void;
  readonly goalkeeperFilterCount: number;
  readonly gkPassingRange: [number, number];
  readonly onSetGkPassingRange: (range: [number, number]) => void;
  readonly gkShotStoppingRange: [number, number];
  readonly onSetGkShotStoppingRange: (range: [number, number]) => void;
  readonly gkPositioningRange: [number, number];
  readonly onSetGkPositioningRange: (range: [number, number]) => void;

  /* --- Report Stats section --- */
  readonly isReportStatsOpen: boolean;
  readonly onToggleReportStats: () => void;
  readonly reportStatsFilterCount: number;
  readonly customMetrics: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  readonly onSetCustomStatRange: (
    name: string,
    range: [number, number]
  ) => void;
}

/**
 * One 0–100 score slider — nine of the rows differ only in these bindings.
 * @param props - The sections' props (for the optional range test id).
 * @param key - React key and test-id key.
 * @param label - Field label.
 * @param value - Current [min, max] draft.
 * @param onChange - Draft setter.
 * @returns The range row.
 */
const renderScoreRange = (
  props: FilterSheetSectionsProps,
  key: string,
  label: string,
  value: [number, number],
  onChange: (range: [number, number]) => void
) => (
  <RangeFilterField
    key={key}
    testID={props.rangeTestID?.(key)}
    label={label}
    value={value}
    minValue={DEFAULT_SCORE_RANGE[0]}
    maxValue={DEFAULT_SCORE_RANGE[1]}
    step={1}
    onChange={onChange}
  />
);

/**
 * BASIC: Roles, Positions, Leagues, Clubs, Nationalities, Age, Months.
 * @param props - The sections' props.
 * @returns The expanded BASIC body.
 */
const renderBasicContent = (props: FilterSheetSectionsProps) => (
  <Stack space="4" className="overflow-visible px-6 pb-4">
    {!props.discoveryOnly && (
      <Stack space="1">
        {filterLabel("Roles")}
        <RoleChipGrid
          options={props.roleOptions}
          selectedIds={props.selectedRoleArchetypes}
          onToggleOption={props.onToggleRole}
        />
      </Stack>
    )}

    <Stack space="1" className={filterZ(props.isPositionFilterOpen)}>
      {filterLabel("Positions")}
      <FilterChip
        label="Position"
        options={props.positionOptions}
        selectedIds={props.selectedPositionIds}
        onToggleOption={props.onTogglePosition}
        onClearAll={props.onClearPositions}
        isOpen={props.isPositionFilterOpen}
        onToggle={props.onTogglePositionFilter}
        onClose={props.onClosePositionFilter}
        loading={props.optionsLoading}
      />
    </Stack>

    {!props.discoveryOnly && (
      <Stack space="1" className={filterZ(props.isLeagueFilterOpen)}>
        {filterLabel("Leagues")}
        <FilterChip
          label="League"
          options={props.leagueOptions}
          selectedIds={props.selectedLeagueIds}
          onToggleOption={props.onToggleLeague}
          onClearAll={props.onClearLeagues}
          isOpen={props.isLeagueFilterOpen}
          onToggle={props.onToggleLeagueFilter}
          onClose={props.onCloseLeagueFilter}
          loading={props.optionsLoading}
        />
      </Stack>
    )}

    <Stack space="1" className={filterZ(props.isClubFilterOpen)}>
      {filterLabel("Clubs")}
      <FilterChip
        label="Club"
        options={props.clubOptions}
        selectedIds={props.selectedClubIds}
        onToggleOption={props.onToggleClub}
        onClearAll={props.onClearClubs}
        isOpen={props.isClubFilterOpen}
        onToggle={props.onToggleClubFilter}
        onClose={props.onCloseClubFilter}
        loading={props.optionsLoading}
        onServerSearch={props.onServerSearchClub}
        searching={props.clubSearching}
      />
    </Stack>

    <Stack space="1" className={filterZ(props.isNationalityFilterOpen)}>
      {filterLabel("Nationalities")}
      <FilterChip
        label="Nationality"
        options={props.nationalityOptions}
        selectedIds={props.selectedNationalityIds}
        onToggleOption={props.onToggleNationality}
        onClearAll={props.onClearNationalities}
        isOpen={props.isNationalityFilterOpen}
        onToggle={props.onToggleNationalityFilter}
        onClose={props.onCloseNationalityFilter}
        loading={props.optionsLoading}
      />
    </Stack>

    <RangeFilterField
      key="age"
      testID={props.rangeTestID?.("age")}
      label="Age"
      value={props.ageRange}
      minValue={DEFAULT_AGE_RANGE[0]}
      maxValue={DEFAULT_AGE_RANGE[1]}
      step={1}
      onChange={props.onSetAgeRange}
    />

    {!props.sportingOnly ? (
      <RangeFilterField
        key="months"
        testID={props.rangeTestID?.("months")}
        label="Months Remaining"
        value={props.contractMonthsRange}
        minValue={DEFAULT_CONTRACT_MONTHS_RANGE[0]}
        maxValue={DEFAULT_CONTRACT_MONTHS_RANGE[1]}
        step={1}
        onChange={props.onSetContractMonthsRange}
      />
    ) : null}
  </Stack>
);

/**
 * FIT & RATINGS: GPR, Player/Team Style Fit and the Current Season GPR
 * placeholder.
 * @param props - The sections' props.
 * @returns The expanded FIT & RATINGS body.
 */
const renderFitRatingsContent = (props: FilterSheetSectionsProps) => (
  <Stack space="4" className="px-6 pb-4">
    {renderScoreRange(props, "gpr", "GPR", props.gprRange, props.onSetGprRange)}
    {!props.discoveryOnly && (
      <>
        {renderScoreRange(
          props,
          "fit",
          "Player Style Fit",
          props.fitScoreRange,
          props.onSetFitScoreRange
        )}
        {renderScoreRange(
          props,
          "teamfit",
          "Team Style Fit",
          props.teamStyleFitRange,
          props.onSetTeamStyleFitRange
        )}
        {/* Current Season GPR — disabled, pending backend support */}
        <Stack space="1" className="opacity-50 pointer-events-none">
          <Text variant="caption" className="text-right text-content-subtle">
            Coming soon
          </Text>
          <RangeFilterField
            label="Current Season GPR"
            value={DEFAULT_SCORE_RANGE}
            minValue={DEFAULT_SCORE_RANGE[0]}
            maxValue={DEFAULT_SCORE_RANGE[1]}
            step={1}
            onChange={NOOP}
          />
        </Stack>
      </>
    )}
  </Stack>
);

/**
 * PERFORMANCE, GOALKEEPER and REPORT STATS — the sections discovery mode
 * hides.
 * @param props - The sections' props.
 * @returns The three sections.
 */
const renderScoutingSections = (props: FilterSheetSectionsProps) => (
  <>
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="PERFORMANCE"
        testID={props.sectionTestID?.("performance")}
        isExpanded={props.isPerformanceOpen}
        onToggle={props.onTogglePerformance}
        activeCount={props.performanceFilterCount}
      />
      {props.isPerformanceOpen ? (
        <Stack space="4" className="px-6 pb-4">
          {renderScoreRange(
            props,
            "carrying",
            "Carrying",
            props.carryingRange,
            props.onSetCarryingRange
          )}
          {renderScoreRange(
            props,
            "dribbling",
            "Dribbling",
            props.dribblingRange,
            props.onSetDribblingRange
          )}
          {renderScoreRange(
            props,
            "passing",
            "Passing",
            props.passingRange,
            props.onSetPassingRange
          )}
          {renderScoreRange(
            props,
            "shooting",
            "Shooting",
            props.shootingRange,
            props.onSetShootingRange
          )}
        </Stack>
      ) : null}
    </Stack>

    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="GOALKEEPER"
        testID={props.sectionTestID?.("goalkeeper")}
        isExpanded={props.isGoalkeeperOpen}
        onToggle={props.onToggleGoalkeeper}
        activeCount={props.goalkeeperFilterCount}
      />
      {props.isGoalkeeperOpen ? (
        <Stack space="4" className="px-6 pb-4">
          {renderScoreRange(
            props,
            "gkpass",
            "GK Passing",
            props.gkPassingRange,
            props.onSetGkPassingRange
          )}
          {renderScoreRange(
            props,
            "gkstop",
            "Shot Stopping",
            props.gkShotStoppingRange,
            props.onSetGkShotStoppingRange
          )}
          {renderScoreRange(
            props,
            "gkpos",
            "Positioning",
            props.gkPositioningRange,
            props.onSetGkPositioningRange
          )}
        </Stack>
      ) : null}
    </Stack>

    {props.customMetrics.length > 0 || props.reportStatsFilterCount > 0 ? (
      <Stack className="border-b border-outline-default">
        <SectionHeader
          title="REPORT STATS"
          testID={props.sectionTestID?.("reportStats")}
          isExpanded={props.isReportStatsOpen}
          onToggle={props.onToggleReportStats}
          activeCount={props.reportStatsFilterCount}
        />
        {props.isReportStatsOpen ? (
          <Stack space="4" className="px-6 pb-4">
            <CustomMetricFilterFields
              customMetrics={props.customMetrics}
              customStatRanges={props.customStatRanges}
              onSetCustomStatRange={props.onSetCustomStatRange}
            />
          </Stack>
        ) : null}
      </Stack>
    ) : null}
  </>
);

/**
 * Renders the Watchlist filter sections — every control, no chrome.
 * @param props - See {@link FilterSheetSectionsProps}.
 * @returns The section list.
 */
const FilterSheetSectionsView = (props: FilterSheetSectionsProps) => (
  <>
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="BASIC"
        testID={props.sectionTestID?.("basic")}
        isExpanded={props.isBasicOpen}
        onToggle={props.onToggleBasic}
        activeCount={props.basicFilterCount}
      />
      {props.isBasicOpen ? renderBasicContent(props) : null}
    </Stack>

    {!props.sportingOnly ? (
      <Stack className="border-b border-outline-default">
        <SectionHeader
          title="FINANCIAL"
          testID={props.sectionTestID?.("financial")}
          isExpanded={props.isFinancialOpen}
          onToggle={props.onToggleFinancial}
          activeCount={props.financialFilterCount}
        />
        {props.isFinancialOpen ? (
          <Stack space="4" className="px-6 pb-4">
            <RangeFilterField
              key="cost"
              testID={props.rangeTestID?.("cost")}
              label="Cost (in €)"
              value={props.valuationRange}
              minValue={DEFAULT_VALUATION_RANGE[0]}
              maxValue={DEFAULT_VALUATION_RANGE[1]}
              step={100_000}
              onChange={props.onSetValuationRange}
              isCurrency
              useLogScale
              formatDisplayValue={formatNumberWithCommas}
            />
          </Stack>
        ) : null}
      </Stack>
    ) : null}

    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="FIT & RATINGS"
        testID={props.sectionTestID?.("fitAndRatings")}
        isExpanded={props.isFitRatingsOpen}
        onToggle={props.onToggleFitRatings}
        activeCount={props.fitRatingsFilterCount}
      />
      {props.isFitRatingsOpen ? renderFitRatingsContent(props) : null}
    </Stack>

    {props.discoveryOnly ? null : renderScoutingSections(props)}
  </>
);

FilterSheetSectionsView.displayName = "FilterSheetSectionsView";

export default memo(FilterSheetSectionsView);
